<template>
    <div class="map-wrapper">
        <div ref="mapContainer" class="map-container"></div>
        <div class="map-controls">
            <button class="fullscreen-btn" @click="toggleFullscreen" title="Pantalla completa">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                </svg>
            </button>
        </div>
    </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet-textpath'
import salmonShipIcon from '@/assets/img/pin-map6.png'

export default {
    name: 'BarcoMap',
    props: {
        ships: {
            type: Array,
            required: true
        },
        showLabels: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            map: null,
            markers: new Map(),
            tileLayer: null,
            isNightMode: null
        }
    },
    watch: {
        ships: {
            handler() {
                this.updateMarkers()
            },
            deep: true
        },
        showLabels() {
            this.toggleTooltips()
        }
    },
    mounted() {
        this.initMap()
        this.updateMarkers()
        this.map.on('zoomend', this.updateIconSizes)

        setInterval(this.checkAndSetTileLayer, 5 * 60 * 1000)
        this.drawParaleloLine()
        this.drawParaleloVerde()
    },
    beforeDestroy() {
        this.map.off('zoomend', this.updateIconSizes)
    },
    methods: {
        initMap() {
            this.map = L.map(this.$refs.mapContainer).setView([-42.5, -73], 8)
            this.checkAndSetTileLayer()
        },
        drawParaleloVerde() {
            const lat = -43.05; // 43° 03′ S
            const lngStart = -75.0;
            const lngEnd = -71.5;

            const line = L.polyline(
                [
                    [lat, lngStart],
                    [lat, lngEnd]
                ],
                {
                    color: 'green',
                    weight: 3,
                    dashArray: '4, 4',
                    opacity: 0.8
                }
            ).addTo(this.map)

            line.setText('Paralelo 43° 03′ S', {
                repeat: false,
                offset: -10,
                attributes: {
                    fill: 'green',
                    'font-weight': 'bold',
                    'font-size': '14px'
                }
            })

            this.greenLine = line
        },
        drawParaleloLine() {
            const lat = -43.3667; // 43° 22′ S
            const lngStart = -75.0;
            const lngEnd = -71.5;

            const line = L.polyline(
                [
                    [lat, lngStart],
                    [lat, lngEnd]
                ],
                {
                    color: 'red',
                    weight: 3,
                    dashArray: '8, 6',
                    opacity: 0.8
                }
            ).addTo(this.map)

            // Texto sobre la línea
            line.setText('Línea FAN 43°22′00" S', {
                repeat: false,
                offset: -10,
                attributes: {
                    fill: 'red',
                    'font-weight': 'bold',
                    'font-size': '14px'
                }
            })

            this.redLine = line
        },
        checkAndSetTileLayer() {
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: 'America/Santiago',
                hour: 'numeric',
                hour12: false
            })
            const parts = formatter.formatToParts(new Date())
            const hour = parseInt(parts.find(p => p.type === 'hour').value)
            const isNight = hour >= 19 || hour < 6

            if (this.isNightMode === isNight) return

            this.isNightMode = isNight

            const url = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
            const attribution = '&copy; OpenStreetMap contributors'

            if (this.tileLayer) {
                this.map.removeLayer(this.tileLayer)
            }

            this.tileLayer = L.tileLayer(url, { attribution })
            this.tileLayer.addTo(this.map)

            // Aplica o quita filtro nocturno
            const container = this.$refs.mapContainer
            if (isNight) {
                container.classList.add('night-mode')
            } else {
                container.classList.remove('night-mode')
            }
        },
        updateMarkers() {
            if (!this.map) return

            const currentIds = new Set()
            const zoom = this.map.getZoom()
            const iconSize = this.getIconSizeForZoom(zoom)

            this.ships.forEach(ship => {
                const key = ship.id
                currentIds.add(key)

                if (this.markers.has(key)) {
                    const marker = this.markers.get(key)
                    this.animateMarker(marker, [ship.lat, ship.lng])

                    //ACTUALIZAR CONTENIDO DEL POPUP
                    const popupContent = this.generatePopupContent(ship)
                    marker.setPopupContent(popupContent)
                } else {
                    const icon = this.createIcon(iconSize)
                    const marker = L.marker([ship.lat, ship.lng], { icon }).addTo(this.map)

                    const popupContent = this.generatePopupContent(ship)
                    marker.bindPopup(popupContent)
                    marker.on('mouseover', () => marker.openPopup())
                    marker.on('mouseout', () => marker.closePopup())
                    this.markers.set(key, marker)
                }
            })

            for (const key of this.markers.keys()) {
                if (!currentIds.has(key)) {
                    this.map.removeLayer(this.markers.get(key))
                    this.markers.delete(key)
                }
            }

            this.toggleTooltips()
        },
        generatePopupContent(ship) {
            return `
      <div class="ship-popup">
        <div class="popup-title">🚢 ${ship.name}</div>
        <div class="popup-row">📍 <strong>Lat:</strong> ${ship.lat.toFixed(5)}</div>
        <div class="popup-row">📍 <strong>Lng:</strong> ${ship.lng.toFixed(5)}</div>
        ${ship.status ? `<div class="popup-row">⚙️ <strong>Estado:</strong> ${ship.status}</div>` : ''}
      </div>
    `
        },
        toggleTooltips() {
            this.markers.forEach((marker, key) => {
                marker.unbindTooltip()
                if (this.showLabels) {
                    const ship = this.ships.find(s => s.id === key)
                    if (ship) {
                        marker.bindTooltip(ship.name, {
                            permanent: true,
                            direction: 'top',
                            offset: [0, -10],
                            className: 'ship-label'
                        })
                    }
                }
            })
        },
        updateIconSizes() {
            const zoom = this.map.getZoom()
            const iconSize = this.getIconSizeForZoom(zoom)

            this.markers.forEach(marker => {
                const newIcon = this.createIcon(iconSize)
                marker.setIcon(newIcon)
            })
        },
        getIconSizeForZoom(zoom) {
            const baseSize = 64
            const scale = zoom / 10
            const size = Math.round(baseSize * scale)
            return [size, size]
        },
        createIcon(size) {
            return L.icon({
                iconUrl: salmonShipIcon,
                iconSize: size,
                iconAnchor: [size[0] / 2, size[1] / 2.5]
            })
        },
        animateMarker(marker, targetLatLng, duration = 1000) {
            const startLatLng = marker.getLatLng()
            const startTime = performance.now()

            const animate = (now) => {
                const elapsed = now - startTime
                const t = Math.min(elapsed / duration, 1)


                const lat = startLatLng.lat + (targetLatLng[0] - startLatLng.lat) * t
                const lng = startLatLng.lng + (targetLatLng[1] - startLatLng.lng) * t
                marker.setLatLng([lat, lng])

                if (t < 1) {
                    requestAnimationFrame(animate)
                }
            }

            requestAnimationFrame(animate)
        },
        toggleFullscreen() {
            const mapWrapper = this.$refs.mapContainer.parentElement
            if (!document.fullscreenElement) {
                mapWrapper.requestFullscreen().then(() => {
                    // Invalidar el tamaño del mapa después de entrar en fullscreen
                    setTimeout(() => {
                        this.map.invalidateSize()
                    }, 100)
                })
            } else {
                document.exitFullscreen().then(() => {
                    // Invalidar el tamaño del mapa después de salir de fullscreen
                    setTimeout(() => {
                        this.map.invalidateSize()
                    }, 100)
                })
            }
        }
    }
}
</script>

<style>
.map-wrapper {
    position: relative;
    width: 100%;
}

.map-controls {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1000;
}

.fullscreen-btn {
    background: white;
    border: 2px solid rgba(0,0,0,0.2);
    border-radius: 2px;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 1px 5px rgba(0,0,0,0.4);
    transition: background-color 0.2s;
}

.fullscreen-btn:hover {
    background: #f4f4f4;
}

.fullscreen-btn:active {
    background: #e0e0e0;
}

.map-wrapper:fullscreen {
    width: 100vw;
    height: 100vh;
    background: black;
    display: flex;
    align-items: center;
    justify-content: center;
}

.map-wrapper:fullscreen .map-container {
    width: 100%;
    height: 100%;
    border-radius: 0;
}

.map-container {
    width: 100%;
    height: 800px;
}

.ship-label {
    background: #003554;
    border: none;
    color: white;
    font-weight: bold;
    pointer-events: none;
    user-select: none;
    border-radius: 4px;
    padding: 4px 8px;
}

.leaflet-popup-tip {
    background: #022b3a;
    /* mismo azul oscuro que el wrapper */
}

.leaflet-popup-content-wrapper {
    width: 150px;
    /* ancho fijo */
    background: #022b3a;
    color: #dbeafe;
    border-radius: 8px;
    padding: 10px;
    /*font-family: 'Segoe UI', sans-serif;*/
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
}

.leaflet-popup-content {
    margin: 0;
    line-height: 1.4;
    font-size: 14px;
    white-space: normal;
    /* asegura que el texto se ajuste al ancho */
}

.ship-popup {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.popup-title {
    font-weight: bold;
    font-size: 16px;
    color: #93c5fd;
    /* azul claro fuerte */
}

.popup-row {
    display: flex;
    align-items: center;
    gap: 6px;
}

.map-container {
    width: 100%;
    height: 800px;
    border-radius: 16px;
    /* Bordes redondeados */
    overflow: hidden;
    /* Asegura que el mapa respete los bordes */
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    /* Sombra suave */
    border: 2px solid #2f394b;
    /* Borde azul oscuro */
    transition: box-shadow 0.3s ease, border-color 0.3s ease;
    /* Suavidad al interactuar */
}

/* Opcional: efecto al pasar el mouse */
.map-container:hover {
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
    border-color: #2563eb;
    /* azul más vibrante */
}

.leaflet-control-attribution {
    font-size: 5px;
    color: #64748b;
    /* gris azulado tenue */
    background: transparent;
    box-shadow: none;
    padding: 2px 6px;
}

.map-container.night-mode {
    filter: invert(100%) hue-rotate(210deg) brightness(1) saturate(1.3);
    transition: filter 0.5s ease;
}
</style>
